<template>
  <SubContent
    class="class-block class-hero"
    paddingClass="p-[2.4rem] sm:p-[3.2rem] lg:p-[4rem]"
  >
    <section class="class-hero__content">
      <div class="class-hero__copy">
        <h1 class="class-hero__title">{{ section.headline || $t("class") }}</h1>

        <p v-if="section.summaryText" class="class-hero__description">
          {{ section.summaryText }}
        </p>

        <div
          v-if="section.highlights && section.highlights.length"
          class="class-hero__highlights"
        >
          <div
            v-for="(highlight, index) in section.highlights"
            :key="`highlight-${index}`"
            class="class-hero__highlight"
          >
            <span class="class-hero__highlight-icon" aria-hidden="true"></span>
            <span class="class-hero__highlight-text">{{ highlight }}</span>
          </div>
        </div>

        <div v-if="section.priceLabel" class="class-hero__pricing-card">
          <p class="class-hero__pricing-label">{{ $t("Price from") }}</p>
          <p class="class-hero__pricing-amount">{{ section.priceLabel }}</p>
        </div>
      </div>

      <div
        v-if="section.mediaGallery && section.mediaGallery.length"
        class="class-hero__media"
      >
        <div class="class-hero__media-main">
          <img
            :src="section.mediaGallery[0]"
            :alt="section.headline || $t('class')"
          />
        </div>
      </div>
    </section>

    <div class="class-block__schedule">
      <h2 class="class-section__title">
        {{ $t("Upcoming sessions") }}
      </h2>
      <div
        v-if="isPrimary && availabilityLoading"
        class="class-section__feedback"
      >
        <InlineSpinner size="20" />
      </div>
      <div
        v-else-if="isPrimary && availabilityError"
        class="class-section__feedback"
      >
        {{ $t("Unable to load availability") }}
      </div>
      <div
        v-else-if="!section.scheduleCards || !section.scheduleCards.length"
        class="class-section__feedback"
      >
        {{ $t("No availability for this specialist") }}
      </div>
      <ClassScheduleList
        v-else
        :schedule-cards="section.scheduleCards"
        :class-data="section.classData"
      />
    </div>
  </SubContent>
</template>

<script>
import SubContent from "@/components/design/subContent.vue";
import ClassScheduleList from "@/components/classes/ClassScheduleList.vue";
import InlineSpinner from "@/components/loader/InlineSpinner.vue";

export default {
  name: "ClassSection",
  components: {
    SubContent,
    ClassScheduleList,
    InlineSpinner,
  },
  props: {
    section: {
      type: Object,
      required: true,
    },
    isPrimary: {
      type: Boolean,
      default: false,
    },
    availabilityLoading: {
      type: Boolean,
      default: false,
    },
    availabilityError: {
      type: Boolean,
      default: false,
    },
  },
};
</script>

<style scoped>
.class-block {
  display: flex;
  flex-direction: column;
  gap: 3.2rem;
}

.class-hero {
  background: #ffffff;
  border: none;
  border-radius: 60px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  backdrop-filter: none;
  -webkit-backdrop-filter: none;
}

.class-hero__content {
  display: grid;
  gap: 3.2rem;
  grid-template-columns: minmax(0, 1.1fr) minmax(0, 1fr);
  align-items: start;
}

.class-hero__copy {
  display: flex;
  flex-direction: column;
  gap: 1.6rem;
  font-family: "Urbanist", sans-serif;
  align-self: stretch;
  height: 100%;
}

.class-hero__title {
  margin: 0;
  font-size: clamp(3.2rem, 3.6vw, 5.6rem);
  font-weight: 500;
  line-height: 1.14;
  letter-spacing: 0.01em;
  color: #2d2244;
}

.class-hero__description {
  margin: 0;
  font-size: 1.6rem;
  line-height: 2.4rem;
  color: rgba(45, 34, 68, 0.7);
}

.class-hero__highlights {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1.2rem 2.4rem;
}

.class-hero__highlight {
  display: flex;
  align-items: center;
  gap: 1.2rem;
}

.class-hero__highlight-icon {
  width: 2rem;
  height: 2rem;
  border-radius: 999px;
  background: rgba(183, 160, 218, 0.15);
  position: relative;
  flex-shrink: 0;
}

.class-hero__highlight-icon::before {
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0.6rem;
  height: 1rem;
  border-right: 2px solid #b7a0da;
  border-bottom: 2px solid #b7a0da;
  transform: translate(-50%, -60%) rotate(45deg);
}

.class-hero__highlight-text {
  font-size: 1.6rem;
  line-height: 2.4rem;
  color: rgba(45, 34, 68, 0.7);
}

.class-hero__pricing-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.6rem;
  background: rgba(183, 160, 218, 0.15);
  border-radius: 20px;
  padding: 1.6rem 2.4rem;
  min-height: 7.2rem;
  width: min(100%, 57.9rem);
  margin-top: auto;
}

.class-hero__pricing-label {
  margin: 0;
  font-size: 1.6rem;
  line-height: 3.2rem;
  color: rgba(45, 34, 68, 0.7);
}

.class-hero__pricing-amount {
  margin: 0;
  font-size: 2.4rem;
  line-height: 3.2rem;
  font-weight: 600;
  color: #2d2244;
}

.class-hero__media {
  display: flex;
  justify-content: flex-end;
}

.class-hero__media-main {
  position: relative;
  width: 100%;
  max-width: 49.2rem;
  aspect-ratio: 492 / 400;
  border-radius: 40px;
  overflow: hidden;
}

.class-hero__media-main img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.class-section__title {
  margin: 0 0 1.6rem;
  font-size: 1.8rem;
  font-weight: 600;
  color: #000000;
}

.class-section__feedback {
  padding: 2.4rem 0;
  color: rgba(45, 34, 68, 0.6);
  font-size: 1.4rem;
  text-align: center;
}

@media (max-width: 1024px) {
  .class-hero {
    border-radius: 32px;
  }

  .class-hero__content {
    grid-template-columns: 1fr;
  }

  .class-hero__media {
    justify-content: flex-start;
  }

  .class-hero__media-main {
    max-width: 100%;
  }
}

@media (max-width: 640px) {
  .class-hero {
    border-radius: 24px;
  }

  .class-hero__title {
    font-size: clamp(2.6rem, 8vw, 3.6rem);
    line-height: 1.2;
  }

  .class-hero__highlights {
    grid-template-columns: 1fr;
  }

  .class-hero__pricing-card {
    padding: 1.2rem 1.6rem;
  }

  .class-hero__pricing-amount {
    font-size: 2rem;
  }
}
</style>
